// ─── La schermata di accesso ─────────────────────────────────────────────────

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DURATA_RIAPERTURA_MINUTI, LUNGHEZZA_MINIMA_PASSWORD } from '@pmo/shared';
import { capacitaAccesso, chiediRiapertura, creaAccesso, entra } from '../../lib/api.js';
import { Campo, CampoPassword, Guasto } from '../../components/pezzi.js';

type Modo = 'entra' | 'primo-accesso' | 'dimenticata';

export function Accesso(): React.JSX.Element {
  const [modo, setModo] = useState<Modo>('entra');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [chiesta, setChiesta] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<unknown>(null);

  /**
   * ⭐ Si CHIEDE al server se la posta è accesa, invece di offrire il bottone e
   * sperare. Senza questa domanda, «ho dimenticato la password» esisterebbe
   * anche su un gestionale senza SMTP: la persona lo preme, legge «ti abbiamo
   * mandato un messaggio», e aspetta una cosa che non partirà mai.
   * 📌 Una funzione spenta che non lo dichiara costa più di una funzione che
   *    non c'è: la seconda fa cercare un'altra strada, la prima fa aspettare.
   */
  const capacita = useQuery({
    queryKey: ['capacita-accesso'],
    queryFn: capacitaAccesso,
    staleTime: Infinity,
    // Se non risponde, si mostra la strada che funziona sempre (chiedere a un
    // proprietario): è il verso in cui sbagliare non lascia nessuno ad aspettare.
    retry: 1,
  });
  const postaAccesa = capacita.data?.passwordResetByEmail ?? false;

  async function manda(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErrore(null);
    setInCorso(true);
    try {
      if (modo === 'entra') await entra(email, password);
      else if (modo === 'primo-accesso') await creaAccesso(email, password);
      else {
        await chiediRiapertura(email);
        setChiesta(true);
      }
      // Non si scrive «✅ entrato»: a dirlo è la schermata che compare, che
      // arriva DOPO la risposta del server (§ 03.7).
    } catch (err) {
      setErrore(err);
    } finally {
      setInCorso(false);
    }
  }

  const vaiA = (m: Modo) => {
    setModo(m);
    setErrore(null);
    setChiesta(false);
  };

  const titoli: Record<Modo, string> = {
    entra: 'Gestionale del circolo',
    'primo-accesso': 'Primo accesso: scegli la tua password',
    dimenticata: 'Riapri il tuo accesso',
  };

  return (
    <main className="accesso">
      <form className="riquadro" onSubmit={manda}>
        <h1>Padel Village</h1>
        <p className="sottotitolo">{titoli[modo]}</p>

        <Campo
          etichetta="Email"
          type="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {modo !== 'dimenticata' && (
          <CampoPassword
            etichetta="Password"
            name="password"
            autoComplete={modo === 'entra' ? 'current-password' : 'new-password'}
            required
            minLength={modo === 'entra' ? undefined : LUNGHEZZA_MINIMA_PASSWORD}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}

        <Guasto errore={errore} />

        {/* ⚖️ Il messaggio è lo STESSO che l'indirizzo esista o no, perché lo è
            anche la risposta del server: se cambiasse, questa schermata
            diventerebbe il modo più comodo per farsi dire quali sono le email
            vere del circolo. Il «se» della prima riga non è una cortesia: è
            l'unica cosa vera che si può dire. */}
        {chiesta && (
          <p className="riuscito">
            Se quell’indirizzo è di un operatore attivo, il messaggio è partito. Il collegamento vale{' '}
            {DURATA_RIAPERTURA_MINUTI} minuti. Guarda anche nella posta indesiderata.
          </p>
        )}

        <button type="submit" disabled={inCorso}>
          {inCorso
            ? 'Un attimo…'
            : modo === 'entra'
              ? 'Entra'
              : modo === 'primo-accesso'
                ? 'Crea l’accesso'
                : 'Mandami il collegamento'}
        </button>

        {/* ⚖️ Non è una registrazione aperta, e il testo lo dice: il server
            accetta solo un'email che un proprietario ha già messo in
            Impostazioni, e a qualunque altra risponde come a una password
            sbagliata. Scriverlo qui evita che qualcuno lo scopra provando. */}
        <button
          type="button"
          className="piatto"
          onClick={() => vaiA(modo === 'entra' ? 'primo-accesso' : 'entra')}
        >
          {modo === 'entra' ? 'È il mio primo accesso' : 'Ho già una password'}
        </button>

        {modo === 'entra' &&
          (postaAccesa ? (
            <button type="button" className="piatto" onClick={() => vaiA('dimenticata')}>
              Ho dimenticato la password
            </button>
          ) : (
            /* ⛔ Il bottone NON compare quando la posta è spenta, e al suo posto
               c'è la strada che funziona lo stesso. Mostrarlo e basta sarebbe
               peggio di non averlo: chi lo preme resta ad aspettare. */
            <p className="tenue piccolo">
              Password dimenticata? Su questo gestionale la posta non è configurata: te lo riapre un
              proprietario da Impostazioni → Utenti staff, e poi rientri con «È il mio primo accesso».
            </p>
          ))}

        {modo === 'primo-accesso' && (
          <p className="tenue piccolo">
            Funziona solo se un proprietario ha già aperto il tuo accesso in Impostazioni → Utenti staff. Se
            non l’ha fatto, chiediglielo: da qui non si crea un utente nuovo.
          </p>
        )}
        {modo === 'dimenticata' && (
          <p className="tenue piccolo">
            Arriva un collegamento che vale una volta sola. Aprendolo scegli una password nuova: nessuno te ne
            manda una, e le sessioni aperte altrove si chiudono.
          </p>
        )}
      </form>
    </main>
  );
}
